import { useState, useEffect, useMemo } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { motion } from 'framer-motion';
import { fetchProducts, verifyProduct } from '../utils/api';

const Home = () => {
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationMessage, setVerificationMessage] = useState({ text: '', type: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All Categories');
  const [allProducts, setAllProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [page, setPage] = useState(1);
  const [isMobile, setIsMobile] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  const productsPerPage = 10;

  useEffect(() => {
    const loadProducts = async () => {
      try {
        setLoading(true);
        const data = await fetchProducts();
        setAllProducts(data);
        const uniqueCategories = [...new Set(data.map((p) => p.category))];
        setCategories(uniqueCategories);
      } catch {
        setError('Failed to load products. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
    setIsMobile(window.innerWidth < 768);
  }, []);

  const filteredProducts = useMemo(() => {
    return allProducts.filter((product) => {
      const matchSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchCategory =
        selectedCategory === 'All Categories' || product.category === selectedCategory;
      return matchSearch && matchCategory;
    });
  }, [searchQuery, selectedCategory, allProducts]);

  const paginatedProducts = useMemo(() => {
    return filteredProducts.slice(0, page * productsPerPage);
  }, [filteredProducts, page]);

  const fetchMoreData = () => {
    if (paginatedProducts.length < filteredProducts.length) {
      setPage((prevPage) => prevPage + 1);
    }
  };

  const handleVerification = async (e) => {
    e.preventDefault();
    if (!verificationCode.trim()) {
      setVerificationMessage({ text: 'Please enter a verification code', type: 'error' });
      return;
    }

    try {
      setIsVerifying(true);
      const response = await verifyProduct(verificationCode.trim());
      setVerificationMessage({ text: response.message, type: response.status });
      setVerificationCode('');
    } catch {
      setVerificationMessage({
        text: 'Failed to verify product. Please try again.',
        type: 'error',
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
  };

  return (
    <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 bg-slate-100 text-text font-sans mt-12 rounded-lg'>
      {/* Header Section */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className='flex flex-col md:flex-row justify-between items-start mb-10 gap-6'
      >
        <form onSubmit={handleSearchSubmit} className='flex flex-wrap items-center gap-2 text-sm'>
          <input
            type='search'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder='Search products...'
            className='flex-grow min-w-[120px] px-2 py-1 border border-gray-300 rounded-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500'
          />
          <select
            value={selectedCategory}
            onChange={(e) => {
              setSelectedCategory(e.target.value);
              setPage(1);
            }}
            className='px-2 py-1 border border-gray-300 rounded-sm bg-primary-light focus:outline-none focus:ring-1 focus:ring-blue-500'
          >
            <option value='All Categories'>All Categories</option>
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
          <button
            type='submit'
            className='px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-sm transition text-sm'
          >
            Search
          </button>
        </form>

        <form
          onSubmit={handleVerification}
          className='flex items-center gap-2 text-sm mt-3 sm:mt-0 w-full sm:w-auto'
        >
          <input
            type='search'
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            placeholder='Enter code'
            className='flex-grow min-w-[120px] px-2 py-1 border border-gray-300 rounded-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500'
          />
          <button
            type='submit'
            disabled={isVerifying}
            className='px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-sm transition text-sm'
          >
            Verify
          </button>
        </form>
      </motion.section>

      {/* Messages */}
      {verificationMessage.text && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={`mb-6 p-3 text-sm rounded-md max-w-xl mx-auto ${
            verificationMessage.type === 'success'
              ? 'bg-accent text-primary border-l-4 border-primary'
              : 'bg-danger/20 text-white border-l-4 border-danger'
          }`}
        >
          {verificationMessage.text}
        </motion.div>
      )}

      {error && (
        <div className='mb-4 p-3 text-sm bg-danger/20 text-danger border-l-4 border-danger rounded-md max-w-4xl mx-auto'>
          {error}
        </div>
      )}

      {/* Product Grid */}
      {loading ? (
        <div className='text-center py-12 text-primary text-sm'>Loading products...</div>
      ) : filteredProducts.length === 0 ? (
        <div className='text-center py-12 text-muted text-sm'>
          No products found matching your criteria.
        </div>
      ) : (
        <InfiniteScroll
          dataLength={paginatedProducts.length}
          next={fetchMoreData}
          hasMore={paginatedProducts.length < filteredProducts.length}
          loader={<h4 className='text-center text-sm text-muted mt-4'>Loading more...</h4>}
        >
          <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6'>
            {paginatedProducts.map((product) => (
              <motion.div
                key={product.id}
                whileHover={{ scale: 1.05 }}
                className='bg-white border border-border rounded-lg p-3 shadow-sm transition-shadow hover:shadow-md'
              >
                <div className='aspect-w-1 aspect-h-1 mb-3 overflow-hidden rounded-md'>
                  <img
                    src={`/products/${product.image}`}
                    alt={product.name}
                    loading='lazy'
                    onError={(e) => {
                      e.currentTarget.src = '/products/placeholder.jpg';
                      e.currentTarget.onerror = null;
                    }}
                    className='object-cover w-full h-full transform transition-transform duration-300 hover:scale-110'
                  />
                </div>
                <h3 className='text-md font-semibold mb-1 text-neutral-dark'>{product.name}</h3>
                <p className='text-primary text-sm font-medium'>{product.category}</p>
              </motion.div>
            ))}
          </div>
        </InfiniteScroll>
      )}
    </div>
  );
};

export default Home;
